import {
  Component,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type {
  ResourceApplyResponse,
  ResourceDetailResponse,
  ResourceDryRunEnvelope,
} from "@k8s-dashboard/contracts";
import {
  DRY_RUN_ABSOLUTE_MAX_BYTES,
  identityKey,
  isAbortError,
  LOCAL_REJECT_MESSAGES,
  localReject,
  MANIFEST_CLIENT_POLICY_MAX_BYTES,
  manifestByteLength,
  responseMatchesDetail,
  REVIEW_IDENTITY_MISMATCH_MESSAGE,
  reviewErrorMessage,
  submitDryRun,
} from "./dryrun";
import { ApplyStep } from "./ApplyStep";
import { ReviewResult } from "./ReviewResult";

const LazyYamlEditor = lazy(() => import("./YamlEditor").then((module) => ({ default: module.YamlEditor })));
const EDITOR_INPUT_LIMIT_MESSAGE = "편집 문서는 UTF-8 1MiB까지입니다. 이번 입력 전체를 적용하지 않았습니다.";
const EDITOR_LOAD_FAILURE_MESSAGE =
  "고급 편집기를 불러오지 못해 기본 텍스트 편집기로 전환했습니다. 유계 실행 취소와 리터럴 검색은 사용할 수 없습니다.";

/**
 * 변경 검토 → 적용 (ADR 0019)
 * --------------------------------------------------------------------------
 * 고친 매니페스트를 **먼저 적용하지 않고** 서버에 물어봅니다 — "이걸 적용하면
 * 무엇이 달라지고 무엇이 막히는가". 그 답이 거절이 아니고 서버가 자격(grant)을
 * 준 경우에만 적용 단계가 나타납니다.
 *
 * raw 매니페스트와 자격이 사는 곳은 이 컴포넌트의 상태와 POST 본문뿐입니다.
 * URL·저장소·쿼리 캐시·로그 어디에도 두지 않고, 탭을 벗어나거나 드로어가 닫히면
 * 언마운트와 함께 사라집니다.
 *
 * **적용 진입점을 화면이 추론하지 않습니다.** 서버가 grant를 주지 않으면 검토만
 * 하는 화면 그대로입니다 — 적용이 꺼진 배포, 대상이 아닌 GVR, 거절된 검토가
 * 전부 같은 경로로 처리됩니다.
 */

/* 계약 상한과 같은 값입니다. 서버가 더 보낼 수 없지만, 화면은 그것을 믿고
   무한히 그리지 않습니다 — 방어적으로 한 번 더 자릅니다. */

type Phase = "idle" | "submitting" | "result" | "error";

/* 계약 enum을 화면 문구로 옮기는 표입니다.
   **모르는 값은 그대로 되비추지 않습니다** — 서버가 무엇을 보내든 DOM에 나가는
   문자열은 아래 표 안의 것뿐입니다. 텍스트든 속성이든 같습니다. */









export interface ChangeReviewPanelProps {
  /** 상세가 서버에서 받아 온 신원과 정제된 YAML입니다. 편집기의 seed이기도 합니다. */
  detail: ResourceDetailResponse;
  /** 적용이 끝나면 상위가 목록·상세를 다시 조회합니다. 없으면 적용 단계도 없습니다. */
  onApplied?: (result: ResourceApplyResponse) => void;
  /** 새 인증 Scope가 검증될 때까지 read-like 요청과 새 mutation 진입을 막습니다. */
  suspended?: boolean;
}

/**
 * 편집 상태입니다. `draft`는 **언제나 1MiB 이하**입니다.
 *
 * 상한을 넘는 입력은 상태에 반영하지 않고 직전 유효 draft를 유지합니다. byte 수는
 * editor가 증분 계산한 값을 함께 넘겨 parent가 문서 전체를 다시 encode하지 않습니다.
 */
interface EditorState {
  draft: string;
  bytes: number;
  inputRejected: boolean;
}

function boundedSeed(yaml: string): EditorState {
  const bytes = manifestByteLength(yaml);
  if (bytes > DRY_RUN_ABSOLUTE_MAX_BYTES) return { draft: "", bytes: 0, inputRejected: true };
  return { draft: yaml, bytes, inputRejected: false };
}

function BasicManifestEditor({
  detail,
  editor,
  disabled,
  status,
  onDraftChange,
  onCompositionChange,
}: {
  detail: ResourceDetailResponse;
  editor: EditorState;
  disabled: boolean;
  status: string;
  onDraftChange: (value: string) => void;
  onCompositionChange: (composing: boolean, owner: object) => void;
}) {
  const compositionOwner = useRef<object>({}).current;

  useEffect(() => () => onCompositionChange(false, compositionOwner), [compositionOwner, onCompositionChange]);

  return (
    <>
      <span className="muted" role="status">{status}</span>
      <textarea
        id="resource-review-manifest"
        aria-label={`${detail.name} 매니페스트 (YAML, 검토용 편집)`}
        className="resource-review__textarea"
        value={editor.draft}
        rows={16}
        spellCheck={false}
        autoComplete="off"
        autoCapitalize="off"
        disabled={disabled}
        onCompositionStart={() => onCompositionChange(true, compositionOwner)}
        onCompositionEnd={() => onCompositionChange(false, compositionOwner)}
        onChange={(event) => {
          if (!(event.nativeEvent as InputEvent).isComposing) onCompositionChange(false, compositionOwner);
          onDraftChange(event.target.value);
        }}
      />
    </>
  );
}

class EditorErrorBoundary extends Component<
  { fallback: ReactNode; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    return this.state.failed ? this.props.fallback : this.props.children;
  }
}

export function ChangeReviewPanel({ detail, onApplied, suspended = false }: ChangeReviewPanelProps) {
  const [editor, setEditor] = useState<EditorState>(() => boundedSeed(detail.yaml));
  const [phase, setPhase] = useState<Phase>("idle");
  const [result, setResult] = useState<ResourceDryRunEnvelope | undefined>(undefined);
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [composing, setComposing] = useState(false);
  const compositionOwnerRef = useRef<object>();
  const yamlCompositionOwner = useRef<object>({}).current;

  const controllerRef = useRef<AbortController | undefined>(undefined);
  const suspendedRef = useRef(suspended);
  suspendedRef.current = suspended;
  /* genRef는 "이 응답이 아직 유효한 시도인가"를 셉니다. */
  const genRef = useRef(0);
  /* currentIdentityRef는 **렌더 중 동기적으로** 갱신합니다. effect에 미루면 대상이
     바뀐 직후 도착한 응답이 옛 값과 비교되어 통과합니다. */
  const identity = identityKey(detail);
  const currentIdentityRef = useRef(identity);
  currentIdentityRef.current = identity;

  const abortInFlight = useCallback(() => {
    controllerRef.current?.abort();
    controllerRef.current = undefined;
    genRef.current += 1;
  }, []);

  const clearOutcome = useCallback(() => {
    setPhase("idle");
    setResult(undefined);
    setMessage(undefined);
  }, []);

  const updateComposition = useCallback((next: boolean, owner: object) => {
    if (next) {
      compositionOwnerRef.current = owner;
      setComposing(true);
      return;
    }
    if (compositionOwnerRef.current !== owner) return;
    compositionOwnerRef.current = undefined;
    setComposing(false);
  }, []);

  /* 대상이 바뀌면 진행 중 요청을 끊고 편집·결과를 새 대상에 맞게 되돌립니다. */
  useEffect(() => {
    abortInFlight();
    setEditor(boundedSeed(detail.yaml));
    compositionOwnerRef.current = undefined;
    setComposing(false);
    clearOutcome();
  }, [identity, detail.yaml, abortInFlight, clearOutcome]);

  /* Scope가 미검증이면 dry-run만 취소하고 결과·grant를 폐기합니다. bounded draft는
     그대로 두므로 같은 partition 복귀 뒤 사용자가 명시적으로 다시 검토할 수 있습니다. */
  useEffect(() => {
    if (!suspended) return;
    abortInFlight();
    clearOutcome();
  }, [abortInFlight, clearOutcome, suspended]);

  /* 언마운트(탭 전환·드로어 닫힘)도 취소입니다. */
  useEffect(() => () => controllerRef.current?.abort(), []);

  const onDraftChange = (value: string, knownBytes?: number) => {
    if (suspendedRef.current) return;
    /* 입력이 바뀌면 이전 결과는 더 이상 이 입력의 결과가 아닙니다. */
    abortInFlight();
    clearOutcome();
    const bytes = knownBytes ?? manifestByteLength(value);
    if (bytes > DRY_RUN_ABSOLUTE_MAX_BYTES) {
      /* controlled fallback도 직전 유효 초안을 유지해 이번 입력 전체를 거절합니다. */
      setEditor((current) => ({ ...current, inputRejected: true }));
      return;
    }
    setEditor({ draft: value, bytes, inputRejected: false });
  };

  const rejectEditorInput = () => {
    abortInFlight();
    clearOutcome();
    setEditor((current) => ({ ...current, inputRejected: true }));
  };

  const draft = editor.draft;

  const onReview = async () => {
    if (suspendedRef.current) return;
    if (composing) {
      abortInFlight();
      setResult(undefined);
      setMessage("IME 입력을 마친 뒤 다시 검토하세요.");
      setPhase("error");
      return;
    }
    if (editor.inputRejected) {
      abortInFlight();
      setResult(undefined);
      setMessage(EDITOR_INPUT_LIMIT_MESSAGE);
      setPhase("error");
      return;
    }
    const reject = localReject(draft);
    if (reject) {
      /* 확실히 거절될 입력은 **요청을 만들지 않습니다.** */
      abortInFlight();
      setResult(undefined);
      setMessage(LOCAL_REJECT_MESSAGES[reject]);
      setPhase("error");
      return;
    }
    abortInFlight();
    const controller = new AbortController();
    controllerRef.current = controller;
    const generation = genRef.current;
    const requestedIdentity = currentIdentityRef.current;
    setResult(undefined);
    setMessage(undefined);
    setPhase("submitting");

    try {
      const response = await submitDryRun({ detail, manifest: draft, signal: controller.signal });
      if (suspendedRef.current || generation !== genRef.current || requestedIdentity !== currentIdentityRef.current) return;
      if (!responseMatchesDetail(response, detail)) {
        /* 다른 대상의 결과를 보여 주는 것보다 아무것도 보여 주지 않는 편이 낫습니다.
           응답 내용은 화면에 넣지 않습니다. */
        setResult(undefined);
        setMessage(REVIEW_IDENTITY_MISMATCH_MESSAGE);
        setPhase("error");
        return;
      }
      setResult(response);
      setPhase("result");
    } catch (error) {
      /* 취소는 사용자가 만든 오류가 아닙니다. */
      if (isAbortError(error, controller.signal)) return;
      if (suspendedRef.current || generation !== genRef.current || requestedIdentity !== currentIdentityRef.current) return;
      setResult(undefined);
      setMessage(reviewErrorMessage(error));
      setPhase("error");
    } finally {
      if (controllerRef.current === controller) controllerRef.current = undefined;
    }
  };

  /* 상한 초과 래치는 phase와 별개로 계속 보입니다 — 다음 유효 입력에서만 풀립니다. */
  const shownError = editor.inputRejected ? EDITOR_INPUT_LIMIT_MESSAGE : phase === "error" ? message : undefined;

  const basicEditor = (status: string) => (
    <BasicManifestEditor
      detail={detail}
      editor={editor}
      disabled={suspended}
      status={status}
      onDraftChange={onDraftChange}
      onCompositionChange={updateComposition}
    />
  );

  return (
    <div className="resource-review">
      <p className="resource-review__disclaimer" role="note">
        <span className="resource-review__glyph" aria-hidden="true">
          i
        </span>
        먼저 검토만 합니다. 서버가 dryRun으로 물어보고 결과를 돌려주며, 적용은 아래에서 이름을 다시 확인한 뒤에만 일어납니다.
      </p>

      <div className="resource-review__editor">
        <span className="resource-review__label">검토할 매니페스트</span>
        <EditorErrorBoundary fallback={basicEditor(EDITOR_LOAD_FAILURE_MESSAGE)}>
          <Suspense fallback={basicEditor("고급 편집기를 불러오는 중입니다.")}>
            <LazyYamlEditor
              value={draft}
              label={`${detail.name} 매니페스트 (YAML, 검토용 편집)`}
              disabled={suspended}
              onChange={onDraftChange}
              onCompositionChange={(next) => updateComposition(next, yamlCompositionOwner)}
              onLimitRejected={rejectEditorInput}
            />
          </Suspense>
        </EditorErrorBoundary>
      </div>

      <div className="resource-review__actions">
        {/* 진행 중에도 비활성화하지 않습니다. 다시 누르면 **이전 요청을 끊고** 새로
            보내는 것이 계약이고, 버튼을 잠그면 그 경로 자체가 닿을 수 없게 됩니다.
            어느 순간에도 살아 있는 요청은 하나뿐입니다. */}
        <button type="button" className="ds-button ds-button--sm" disabled={suspended || composing} onClick={() => void onReview()}>
          변경 검토
        </button>
        <span className="resource-review__bytes muted">
          {editor.bytes.toLocaleString()}바이트 · 클라이언트 전송 정책{" "}
          {(MANIFEST_CLIENT_POLICY_MAX_BYTES / 1024).toLocaleString()}KiB · 서버 절대 상한 1MiB
        </span>
      </div>

      {phase === "idle" && !shownError && (
        <div className="state">
          <span className="state__glyph" aria-hidden="true">
            →
          </span>
          <span className="state__title">아직 검토하지 않았습니다</span>
          <span className="state__detail">매니페스트를 고치고 “변경 검토”를 누르세요.</span>
        </div>
      )}

      {phase === "submitting" && !shownError && (
        <div className="state" aria-busy="true">
          <span className="state__title">검토하는 중…</span>
        </div>
      )}

      {shownError && (
        <div className="state state--error" role="alert">
          <span className="state__glyph" aria-hidden="true">
            ✕
          </span>
          <span className="state__title">검토하지 못했습니다</span>
          <span className="state__detail">{shownError}</span>
        </div>
      )}

      {!suspended && phase === "result" && result && !editor.inputRejected && (
        <>
          <ReviewResult result={result} />
          {/* 자격이 있을 때만 적용 단계가 생깁니다. key가 자격이라 새 검토는 확인
              입력과 결과를 처음부터 시작합니다 — 옛 확인이 새 자격에 붙지 않습니다. */}
          {result.grant && onApplied && (
            <ApplyStep
              key={result.grant.token}
              detail={detail}
              grant={result.grant}
              onApplied={(applied) => {
                if (!suspendedRef.current) onApplied(applied);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}
